import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useVisibilityRefresh,
  useCombinedRefetch,
  DEFAULT_POLLING_INTERVAL,
} from "../../hooks/useRealtimeQuery";
import {
  selectClinicId,
  selectTherapistId,
} from "../../store/slices/authSlice";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
} from "../../store/services/notificationsApi";
import {
  Bell,
  Eye,
  Checks,
  CalendarCheck,
  Users,
  CurrencyCircleDollar,
  ListChecks,
  Warning,
  FileText,
  BellRinging,
} from "@phosphor-icons/react";
import "./NotificationsDropdown.css";

// Notification type icons
const TYPE_ICONS = {
  appointment: CalendarCheck,
  patient: Users,
  payment: CurrencyCircleDollar,
  task: ListChecks,
  system: Warning,
  treatment: FileText,
  reminder: BellRinging,
};

function NotificationsDropdown() {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications with real-time updates
  const { data: notificationsData, refetch: refetchNotifications } = useGetNotificationsQuery(
    { clinicId, therapistId, pageSize: 20 },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  // Fetch unread count with real-time updates
  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useGetUnreadCountQuery(
    { clinicId, therapistId },
    { skip: !clinicId || !therapistId, pollingInterval: DEFAULT_POLLING_INTERVAL }
  );

  // Combine refetch functions for visibility refresh
  const combinedRefetch = useCombinedRefetch([refetchNotifications, refetchUnreadCount]);

  // Auto-refresh when tab becomes visible
  useVisibilityRefresh(combinedRefetch);

  // Mutations
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead, { isLoading: isMarkingAll }] =
    useMarkAllAsReadMutation();

  // Filter unread notifications
  const notifications = (notificationsData?.notifications || []).filter(
    (n) => !n.readBy?.includes(therapistId)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "עכשיו";
    if (diffMins < 60) return `לפני ${diffMins} דק׳`;
    if (diffHours < 24) return `לפני ${diffHours} שע׳`;
    if (diffDays === 1) return "אתמול";
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    return date.toLocaleDateString("he-IL", { day: "numeric", month: "short" });
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read
    try {
      await markAsRead({
        clinicId,
        notificationId: notification.id,
        therapistId,
      });
    } catch (error) {
      console.error("[CK-0002] Error marking notification as read:", error);
    }

    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  // Handle mark as read (without navigation)
  const handleMarkAsRead = async (e, notification) => {
    e.stopPropagation();
    try {
      await markAsRead({
        clinicId,
        notificationId: notification.id,
        therapistId,
      });
    } catch (error) {
      console.error("[CK-0003] Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead({ clinicId, therapistId });
    } catch (error) {
      console.error("[CK-0004] Error marking all notifications as read:", error);
    }
  };

  // Get icon for notification type
  const getTypeIcon = (type) => {
    const Icon = TYPE_ICONS[type] || Bell;
    return <Icon size={16} weight="duotone" />;
  };

  // Get urgency class
  const getUrgencyClass = (urgency) => {
    if (urgency === 3) return "urgency-high";
    if (urgency === 2) return "urgency-medium";
    return "urgency-low";
  };

  return (
    <div className="notifications-dropdown-container" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        className={`header-btn notification-btn ${isOpen ? "active" : ""} ${unreadCount > 0 ? "has-notifications" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="התראות"
        aria-expanded={isOpen}
      >
        <Bell size={20} weight={isOpen ? "fill" : "regular"} />
        <span className="notification-btn-label">התראות</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="ntf-panel" role="dialog" aria-label="התראות">
          {/* Header */}
          <div className="ntf-header">
            <div className="ntf-header-icon">
              <Bell size={17} weight="fill" />
            </div>
            <span className="ntf-header-title">התראות</span>
            {notifications.length > 0 && (
              <span className="ntf-header-count">{notifications.length}</span>
            )}
          </div>

          {/* Notifications List */}
          <div className="ntf-body">
            {notifications.length === 0 ? (
              <div className="ntf-empty">
                <Bell size={32} weight="light" />
                <p>אין התראות חדשות</p>
              </div>
            ) : (
              <div className="ntf-list">
                {notifications.slice(0, 10).map((notification) => (
                  <div
                    key={notification.id}
                    className={`ntf-item ${getUrgencyClass(notification.urgency)}`}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="ntf-item-icon">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="ntf-item-content">
                      <div className="ntf-item-top">
                        <span className="ntf-item-title">{notification.title}</span>
                        <span className="ntf-item-time">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="ntf-item-message">{notification.message}</p>
                    </div>
                    <button
                      className="ntf-item-read"
                      onClick={(e) => handleMarkAsRead(e, notification)}
                      title="סמן כנצפה"
                      aria-label="סמן כנצפה"
                    >
                      <Eye size={16} weight="regular" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - Mark All + View All */}
          {notifications.length > 0 && (
            <div className="ntf-footer">
              <button
                className="ntf-footer-btn is-primary"
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAll}
              >
                <Checks size={15} weight="bold" />
                <span>סמן הכל כנקרא</span>
              </button>
              {notifications.length > 10 && (
                <button
                  className="ntf-footer-btn is-secondary"
                  onClick={() => {
                    navigate("/dashboard");
                    setIsOpen(false);
                  }}
                >
                  צפה בכל ההתראות ({notifications.length})
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationsDropdown;

